import * as React from 'react'
import Box from '@mui/material/Box'
import { Typography } from '@mui/material'

import { ScreenContext } from 'apps/utils/ScreenContext'
import { usePageTitle } from 'apps/utils/usePageTitle'
import Wrapper from 'apps/common/Wrapper'

import { AboutUsA, AboutUsC, AboutUsT } from './icons'
import { RunicLogo } from './RunicLogo'
import { useStyles } from './styles'

const domains = [
	'Supply Chain',
	'Logistics',
	'Digital Automation',
	'Transportation',
	'Service Scheduling',
	'Sales & Payments',
	'On-prem Software',
	'SaaS',
	'Government Agencies',
	'HR Management',
]

// Ambient highlight cycle (no click affordance - these tiles aren't
// interactive): clockwise, top row left-to-right then bottom row
// right-to-left, one tile per step, looping.
const CYCLE_ORDER = [ 0, 1, 2, 3, 4, 9, 8, 7, 6, 5 ]
const CYCLE_STEP_MS = 400

const AboutUs = () => {
	usePageTitle( 'About — ACT Software' )

	const classes = useStyles()
	const { isLaptop } = React.useContext( ScreenContext )

	const [ cycleStep, setCycleStep ] = React.useState( 0 )
	React.useEffect( () => {
		const id = setInterval(
			() => setCycleStep( ( step ) => ( step + 1 ) % CYCLE_ORDER.length ),
			CYCLE_STEP_MS
		)
		return () => clearInterval( id )
	}, [] )
	const activeDomainIndex = CYCLE_ORDER[ cycleStep ]

	return (
		<Wrapper footerNavClassName={ classes.footerNav }>
			<Box display='grid' gridTemplateColumns={ isLaptop ? '185px auto' : 'auto' }>
				{ isLaptop &&
					<Box
						width='185px'
						left='0'
						top='0'
						bottom='0'
						textTransform='uppercase'
						paddingTop='127px'
						overflow='hidden'
						display='flex'
						justifyContent='center'
						alignItems='flex-start'
					>
						<Typography
							fontSize='10vw'
							fontWeight='bold'
							color='var(--ink)'
							sx={{
								writingMode: 'tb',
								transform: 'scale(-1)',
								textTransform: 'uppercase'
							}}
						>
							About
						</Typography>
					</Box>
				}
				<Box
					maxWidth={ isLaptop ? '1085px' : '100%' }
					marginRight={ isLaptop ? '104px' : '0' }
					borderRight='1px solid var(--line)'
					borderLeft='1px solid var(--line)'
				>
					<Box display='flex' flexDirection='column'>
						<Box
							display='flex'
							justifyContent='space-between'
							position='relative'
						>
							<Box flex='0 0 42.8966789668%' marginLeft='-4px'>
								<AboutUsA />
							</Box>
							{ /* Runic "ACT software" mark, sitting in the gap between
							     the two flanking icons. Inlined SVG so hover can color
							     the letters (see .act-runic rules in base.scss). */ }
							<Box
								sx={{
									position: 'absolute',
									top: '24px',
									left: '50%',
									transform: 'translateX(-50%)',
									width: isLaptop ? '21%' : '28%'
								}}
							>
								<RunicLogo />
							</Box>
							<Box flex='0 0 42.8966789668%'>
								<AboutUsT />
							</Box>
						</Box>
						<Box
							flex='0 0 42.8966789668%'
							position='relative'
							margin='auto'
							width='42.8966789668%'
							sx={{
								transform: 'translate(0, -50%)'
							}}
						>
							<AboutUsC />
						</Box>
					</Box>
					<Box paddingLeft={ isLaptop ? '6.5498154982%' : '16px' } paddingRight={ isLaptop ? '6.5498154982%' : '16px' } marginTop={ isLaptop ? '-96px' : '-48px' }>
						<Typography
							variant='h2'
							textAlign='center'
							sx={{
								marginBottom: '24px',
								textTransform: 'none'
							}}
						>
							{ "Domains we've been serving" }
						</Typography>
					</Box>
					<Box
						paddingLeft={ isLaptop ? '6.5498154982%' : '16px' }
						paddingRight={ isLaptop ? '6.5498154982%' : '16px' }
						marginBottom={ isLaptop ? '48px' : 8 }
						textAlign='center'
					>
						We believe that digital automation will lead humanity to a more civilized age.
					</Box>
					<Box
						className={ isLaptop ? classes.domainRibbon : classes.domainColumn }
						marginBottom={ isLaptop ? '78px' : 8 }
					>
						{ domains.map( ( label, index ) => {
							const row = Math.floor( index / 5 )
							const pos = index % 5
							// The second row is the first one flipped: cells under the
							// row-1 ups point down (mating flat-to-flat with them) and
							// vice versa. Both rows keep the same 36-unit stagger by
							// position, so the vertical offset formula is shared.
							const pointsUp = row === 0 ? pos % 2 === 0 : pos % 2 !== 0
							const isActive = index === activeDomainIndex
							return (
								<Box
									key={ index }
									className={ `${ classes.domainPentagon } ${ pointsUp ? classes.domainPentagonUp : classes.domainPentagonDown }` }
									style={ {
										...( isLaptop ? {
											left: `${ ( pos * 19.1589 ).toFixed( 4 ) }%`,
											top: `${ ( ( row * 95 + ( pos % 2 === 0 ? 0 : 36 ) ) / 2.26 ).toFixed( 3 ) }%`
										} : {} ),
										...( isActive ? { backgroundColor: 'var(--hover)' } : {} )
									} }
								>
									{ /* clip-path only crops the fill/background — it does not
									     make `border` trace the polygon edges, so the outline
									     needs an actual drawn shape instead. */ }
									<svg
										className={ classes.domainPentagonOutline }
										viewBox='0 0 100 95'
										preserveAspectRatio='none'
									>
										<polygon
											points={ pointsUp
												? '50,0 100,36 82,95 18,95 0,36'
												: '50,95 100,59 82,0 18,0 0,59' }
											fill='none'
											stroke='var(--line)'
											strokeWidth='1'
											vectorEffect='non-scaling-stroke'
										/>
									</svg>
									<Typography
										color='inherit'
										fontWeight='700'
										sx={{
											position: 'relative',
											fontSize: isLaptop ? '20px' : '15px',
											textTransform: 'none',
											lineHeight: 1.2,
											padding: '0 12%',
											// Nudge toward the pentagon's visual centroid, which
											// sits below center for point-up and above for
											// point-down shapes.
											marginTop: pointsUp ? '9%' : '-9%'
										}}
									>
										{ label }
									</Typography>
								</Box>
							)
						} ) }
						{ /* Fillers for the only holes the flat-mated rows leave open:
						     one small triangle either side of each row-1 hanging tip.
						     Every filler edge lies on a pentagon edge — the row-1
						     down-tile's lower edge, the row-2 down-tile's side edge,
						     and (a collinear stretch of) the row-2 up-tile's upper
						     edge. Fillers mirror left/right around each tip. */ }
						{ isLaptop && [ 19.1589, 30.8411, 57.4766, 69.1589 ].map( ( left, i ) => (
							<Box key={ `filler-${ i }` } className={ classes.interRowRhombus } style={{ left: `${ left }%` }}>
								<svg
									viewBox='0 0 50 59'
									preserveAspectRatio='none'
									width='100%'
									height='100%'
								>
									<polygon
										points={ i % 2 === 0
											? '0,0 50,36 18,59'
											: '50,0 0,36 32,59' }
										fill='var(--ink)'
										stroke='var(--line)'
										strokeWidth='1'
										vectorEffect='non-scaling-stroke'
									/>
								</svg>
							</Box>
						) ) }
					</Box>
					<Box
						paddingLeft={ isLaptop ? '6.5498154982%' : '16px' }
						paddingRight={ isLaptop ? '6.5498154982%' : '16px' }
						marginBottom={ isLaptop ? '78px' : 8 }
						textAlign='center'
					>
						Should you be interested in our services, please contact{ ' ' }
						<Box
							component='a'
							href='https://amtoss.com'
							target='_blank'
							rel='noopener noreferrer'
							sx={ { color: 'inherit', textDecoration: 'underline', cursor: 'var(--cursor-pointer)' } }
						>
							Amtoss
						</Box>.
					</Box>
					<Box
						onClick={ () => window.scrollTo( { top: 0, behavior: 'smooth' } ) }
						role='button'
						borderTop='1px solid var(--line)'
						height='64px'
						display='flex'
						alignItems='center'
						justifyContent='center'
						fontSize={ 18 }
						fontWeight={ 700 }
						color='var(--ink)'
						sx={ {
							cursor: 'var(--cursor-pointer)',
							transition: 'background-color 0.25s ease',
							'&:hover': { backgroundColor: 'var(--hover)' }
						} }
					>
						<Box component='span' fontSize={ 26 } fontWeight={ 400 } marginRight='28px'>⬠</Box>
						Lift me up
						<Box component='span' fontSize={ 26 } fontWeight={ 400 } marginLeft='28px'>⬠</Box>
					</Box>
				</Box>
			</Box>
		</Wrapper>
	)
}

export default React.memo( AboutUs )
